import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { usePermission, useAnyPermission } from '../../../context/PermissionContext';
import { listLabOrdersForVisit, cancelLabOrder } from '../../../services/api/labApi';
import { ADMISSION_PERMISSIONS, ADMISSION_ACCESS_ERROR_CODE, isInvoiceBearing, isMoneyHidden } from '../../../constants/admission';
import { formatRupees } from '../../../utils/money';
import { formatDateIST } from '../../../utils/formatIST';
import ConfirmationModal from '../../common/ConfirmationModal/ConfirmationModal';
import NoAccessPanel, { NO_ACCESS_VARIANT } from '../../common/NoAccessPanel';
import {
  DIAGNOSTICS_ROW_STATE, diagnosticsRowState, diagnosticsTotals,
  sortNewestFirst, filterBySearch, describeAttender,
} from './diagnosticsModel';

const PAGE_SIZE = 10;
// Once the list exceeds this, a search box appears (house long-list pattern,
// mirrors SettleCreditsCard's `entries.length > PAGE_SIZE - 5` threshold).
const SEARCH_THRESHOLD = PAGE_SIZE;

const chipCls = {
  [DIAGNOSTICS_ROW_STATE.UNPAID]: 'text-[11px] font-bold px-2 py-0.5 rounded-full border border-amber-400 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 whitespace-nowrap',
  [DIAGNOSTICS_ROW_STATE.PAID]: 'text-[11px] font-bold px-2 py-0.5 rounded-full border border-emerald-400 dark:border-emerald-600 bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 whitespace-nowrap',
  [DIAGNOSTICS_ROW_STATE.CANCELLED]: 'text-[11px] font-bold px-2 py-0.5 rounded-full border border-red-400 dark:border-red-600 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 whitespace-nowrap',
};
const chipLabel = {
  [DIAGNOSTICS_ROW_STATE.UNPAID]: 'Unpaid',
  [DIAGNOSTICS_ROW_STATE.PAID]: 'Paid',
  [DIAGNOSTICS_ROW_STATE.CANCELLED]: 'Cancelled',
};

/**
 * DiagnosticsCard (M2) — the one place lab work is handled for an admitted
 * patient: patient + attender at a glance, every test recorded for the stay
 * with what it cost, whether it's paid, order + cancel.
 *
 * ACCOUNTING SURFACE, NOT A WORKFLOW ONE (D24 — the lab module is
 * accounting-only today). Rows show ONLY test/qty/cost/Paid-Unpaid-Cancelled/
 * Ordered date — no technician, no sample number, no advised/collected/
 * in-progress/done. Row state derives from `diagnosticsModel` — cancelled
 * from `status`, paid from `is_paid` (server-derived from `payment_id`,
 * NEVER from `status` — a paid test still reads `status: 'advised'` for
 * ever, see the model file's header comment).
 *
 * WRAPS the existing OrderLabDialog — "Add test" raises
 * `onAction('orderLab', admission)` (the desk's existing dialog mount) and
 * mounts no dialog of its own; a second live copy of a money-taking screen
 * is how double charges happen.
 *
 * ONE bounded fetch keyed on the primitive `admissionId` (mount/unmount via
 * the tab switch is the refetch boundary — no polling, no object/array in a
 * dep array, so it cannot re-fire on every render).
 *
 * C2 — `diagnosticsRefreshToken` (optional, integer, monotonic) is the
 * desk's signal that an order was just placed elsewhere (OrderLabDialog's
 * `onOrdered`). It rides in `load`'s dep array alongside `admissionId` — a
 * primitive, never an object/array, so it cannot re-fire on a re-render;
 * bumping it costs exactly one extra fetch.
 *
 * admission-access (bk-1629) — the list response carries `money_visible`; on
 * `false` the server nulled every amount / paid signal, and this card shows no
 * per-test amount, no "Charged this stay" / unpaid total and no Paid/Unpaid
 * chip (Cancelled stays — it is not money). Keyed on that flag (the field that
 * MEANS it), never on "the amounts are null"; `undefined` renders as today.
 */
export default function DiagnosticsCard({ admission, onAction, diagnosticsRefreshToken }) {
  const admissionId = admission.admission_id;
  // QA-CTF-1 — own slug OR ACCESS_IPD, mirroring the backend's
  // require_any_permission("ORDER_LAB", "ORDER_ADMISSION_LAB", "ACCESS_IPD")
  // on POST /lab/orders. ACCESS_IPD is what survives the per-member
  // Manage-Admission revoke (D27) — without it here, the IPD mount's own
  // "+ Order Diagnostic" button is unreachable for exactly the member ACCESS_IPD
  // was minted for. Safe on the Manage Admission mount too — NOT because the
  // route is gated (it isn't; the tab carries no permission guard) but
  // because the admission the API is refusing to widen for. `GET
  // /admissions/{id}` (and the default `GET /admissions` list) stay
  // VIEW_ADMISSION-only (organisation_admissions.py) — untouched by the
  // ACCESS_IPD widening, which only reaches the `?view=picker` branch and
  // the lab/bill routes. A member who kept only ACCESS_IPD gets a 403 on
  // that fetch, so the mount never receives an `admission` to render this
  // card with — never lands on the widened button because there is nothing
  // to mount it on, not because the tab itself is hidden.
  const canOrderLab = useAnyPermission(
    ADMISSION_PERMISSIONS.ORDER_ADMISSION_LAB,
    ADMISSION_PERMISSIONS.ACCESS_IPD,
  );
  // Owner ruling — "no lab should be allowed to enter after discharge is
  // initiated." Mirrors the server's advise-time guard (same shared
  // `isInvoiceBearing` — the SAME constant C3b's cancel guard already
  // reuses, never re-derived). The server refuses the write regardless;
  // this only stops staff clicking into that refusal.
  const orderBlockedByDischarge = isInvoiceBearing(admission.status);
  // C3b — mirrors the server's discharge-initiated cancel guard (C3a). The
  // SERVER is the wall; this only stops staff clicking into a refusal. Never
  // the only enforcement — a raced cancel still surfaces the server's own
  // 409 verbatim via `cancelError` below.
  const canCancel = usePermission(ADMISSION_PERMISSIONS.CANCEL_LAB_ORDER) && !isInvoiceBearing(admission.status);

  const [orders, setOrders] = useState(null); // null = loading
  // The lab list's own `money_visible` (not the admission's — a lab technician
  // keeps lab amounts the admission-level flag would hide).
  const [moneyVisible, setMoneyVisible] = useState(undefined);
  const [error, setError] = useState(null);
  // admission-access — the department wall refused this stay: the shared panel, not an error box.
  const [outOfScope, setOutOfScope] = useState(false);
  const [q, setQ] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  // Per-row cancel flow: target order + typed reason + in-flight + last error.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelError, setCancelError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    const res = await listLabOrdersForVisit(`labadm_${admissionId}`);
    if (res.success) { setMoneyVisible(res.money_visible); setOrders(res.data); }
    else if (res.code === ADMISSION_ACCESS_ERROR_CODE.OUT_OF_DEPARTMENT_SCOPE) setOutOfScope(true);
    else { setOrders([]); setError(res.error || 'Failed to load this stay’s diagnostics.'); }
    // diagnosticsRefreshToken is not read in the body — it rides here ONLY to
    // change `load`'s identity so the effect below re-fires exactly once per
    // bump (the standing pattern for a refresh signal, see AdmissionDeskPage's
    // own `partitionsKey`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admissionId, diagnosticsRefreshToken]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [q]);

  const moneyHidden = isMoneyHidden({ money_visible: moneyVisible });

  const sorted = useMemo(() => sortNewestFirst(orders || []), [orders]);
  const filtered = useMemo(() => filterBySearch(sorted, q), [sorted, q]);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = filtered.length > visible.length;

  // Single source feeding both the totals line and every row chip.
  const totals = useMemo(() => diagnosticsTotals(orders || []), [orders]);

  const attender = describeAttender(admission.attender);

  const openCancel = (order) => {
    setCancelTarget(order);
    setCancelReason('');
    setCancelError(null);
  };
  const closeCancel = () => {
    if (cancelBusy) return;
    setCancelTarget(null);
    setCancelReason('');
  };

  const confirmCancel = async () => {
    if (!cancelTarget || !cancelReason.trim() || cancelBusy) return;
    setCancelBusy(true);
    const res = await cancelLabOrder(cancelTarget.lab_order_id, { reason: cancelReason.trim() });
    setCancelBusy(false);
    setCancelTarget(null);
    setCancelReason('');
    if (!res.success) {
      // RATIFIED (copy pack) — the discharge-initiated race keys off the
      // machine code, never the server's own prose (matches the D-4 rule
      // this card already follows for its other refusals).
      setCancelError(res.code === 'ADMISSION_DISCHARGE_INITIATED'
        ? 'Discharge has already been started for this patient, so diagnostic orders can no longer be cancelled.'
        : res.error || 'Could not cancel this test.');
    } else {
      setCancelError(null);
    }
    // Always reload — success needs the fresh row; a 409 (e.g. someone else
    // just settled it) needs the fresh row too, or the button would keep
    // offering a cancel the server will refuse again.
    load();
  };

  if (outOfScope) return <NoAccessPanel variant={NO_ACCESS_VARIANT.OUT_OF_DEPARTMENT} />;

  return (
    <div className="p-4" data-testid={`diagnostics-card-${admissionId}`}>
      <div className="rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">🧪 Diagnostics</h3>
          {canOrderLab && (
            <button
              type="button"
              disabled={orderBlockedByDischarge}
              title={orderBlockedByDischarge
                ? "Discharge already initiated — no new diagnostic order can be placed for this stay."
                : undefined}
              onClick={() => { if (!orderBlockedByDischarge) onAction('orderLab', admission); }}
              className="px-3 py-1.5 rounded-lg bg-teal-600 text-white text-[12.5px] font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="diag-order-btn"
            >
              + Order Diagnostic
            </button>
          )}
        </div>

        <div className="text-[15px] font-bold text-slate-900 dark:text-white">{admission.patient_name || admission.patient_id}</div>

        <div className="mt-2.5 px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700" data-testid="diag-attender">
          <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-1">Attender</div>
          {attender.present ? (
            <>
              <div className="text-[13px] text-slate-700 dark:text-slate-200">
                <b className="text-slate-900 dark:text-white">{attender.name}</b>{attender.relation ? ` (${attender.relation})` : ''}{attender.mobile ? ` · ${attender.mobile}` : ''}
              </div>
              {!attender.mobile && (
                <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-0.5" data-testid="diag-attender-no-phone">No phone number on file.</div>
              )}
            </>
          ) : (
            <div className="text-[13px] italic text-slate-400 dark:text-slate-500" data-testid="diag-attender-empty">No attender recorded</div>
          )}
        </div>

        <div className="my-3 border-t border-slate-200 dark:border-slate-700" />

        {cancelError && (
          <div className="mb-3 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="diag-cancel-error">
            {cancelError}
          </div>
        )}

        {orders === null ? (
          <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="diag-loading">Loading this stay's diagnostics…</p>
        ) : error ? (
          <div className="rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-[12.5px] p-2.5" data-testid="diag-error">{error}</div>
        ) : orders.length === 0 ? (
          <div className="text-center border border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6" data-testid="diag-empty">
            <div className="text-[13px] font-bold text-slate-700 dark:text-slate-200 mb-1">No diagnostic tests ordered yet for this stay</div>
            <div className="text-[12.5px] text-slate-500 dark:text-slate-400">Nothing has been charged for tests since admission.</div>
          </div>
        ) : (
          <>
            {!moneyHidden && totals && (
              <div className="text-[12.5px] text-slate-600 dark:text-slate-300 mb-2" data-testid="diag-totals">
                Charged this stay: <b className="text-slate-900 dark:text-white">{formatRupees(totals.totalCharged)}</b>
                {totals.unpaid > 0 && <> — <span className="font-bold text-amber-700 dark:text-amber-300">{formatRupees(totals.unpaid)} unpaid</span></>}
              </div>
            )}

            <div className="flex items-center justify-between mb-2">
              <span className="text-[12.5px] font-bold text-slate-700 dark:text-slate-200">Tests this stay</span>
              <span className="text-[11.5px] text-slate-500 dark:text-slate-400">{filtered.length} total</span>
            </div>

            {orders.length > SEARCH_THRESHOLD && (
              <input
                type="text"
                className="w-full mb-2 px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
                placeholder="Search this stay's tests…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="diag-search"
              />
            )}

            {filtered.length === 0 ? (
              <div className="text-[12.5px] text-slate-500 dark:text-slate-400 text-center py-4" data-testid="diag-no-match">{`No test matches "${q}".`}</div>
            ) : (
              <div className="space-y-1.5" data-testid="diag-rows">
                {visible.map((o) => {
                  const state = diagnosticsRowState(o, moneyHidden);
                  const cancelled = state === DIAGNOSTICS_ROW_STATE.CANCELLED;
                  const paid = state === DIAGNOSTICS_ROW_STATE.PAID;
                  return (
                    <div
                      key={o.lab_order_id}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg border border-slate-200 dark:border-slate-700 ${cancelled ? 'opacity-60' : ''}`}
                      data-testid={`diag-row-${o.lab_order_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className={`text-[13px] font-bold text-slate-800 dark:text-slate-100 truncate ${cancelled ? 'line-through' : ''}`}>
                          {o.test_name}{o.quantity > 1 ? ` ×${o.quantity}` : ''}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400">
                          {[
                            o.origin_doctor_name ? `Ordered by Dr. ${o.origin_doctor_name}` : null,
                            formatDateIST(o.created_at) ? `Ordered ${formatDateIST(o.created_at)}` : null,
                          ].filter(Boolean).join(' · ')}
                        </div>
                        {paid && canCancel && (
                          <div className="text-[11px] italic text-slate-500 dark:text-slate-400 mt-0.5" data-testid={`diag-blocked-${o.lab_order_id}`}>
                            Already paid — this can no longer be cancelled.
                          </div>
                        )}
                      </div>
                      {!moneyHidden && (
                        <b className={`text-[13px] tabular-nums ${cancelled ? 'line-through text-slate-400 dark:text-slate-500 font-semibold' : 'text-slate-900 dark:text-white'}`} data-testid={`diag-amount-${o.lab_order_id}`}>
                          {formatRupees(o.final_cost)}
                        </b>
                      )}
                      {state && <span className={chipCls[state]} data-testid={`diag-chip-${o.lab_order_id}`}>{chipLabel[state]}</span>}
                      {!cancelled && canCancel && (
                        <button
                          type="button"
                          disabled={paid}
                          title={paid ? 'Already paid — this can no longer be cancelled.' : undefined}
                          onClick={() => openCancel(o)}
                          className="px-2.5 py-1 rounded-md border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-[11.5px] font-bold disabled:opacity-50 disabled:cursor-default"
                          data-testid={`diag-cancel-${o.lab_order_id}`}
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  );
                })}
                {hasMore && (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                    className="w-full px-3 py-2 rounded-lg border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 text-[12.5px] font-semibold"
                    data-testid="diag-show-more"
                  >
                    {`Show ${Math.min(PAGE_SIZE, filtered.length - visible.length)} more`}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {cancelTarget && (
        <ConfirmationModal
          isOpen
          icon="✕"
          variant="danger"
          title="Cancel this diagnostic order?"
          message={(
            <>
              {`${cancelTarget.test_name} for ${admission.patient_name || admission.patient_id}${moneyHidden ? '' : ` (${formatRupees(cancelTarget.final_cost)})`} will be cancelled and removed from the diagnostics bill. This cannot be undone.`}
              <label className="block text-left text-[11.5px] font-semibold text-slate-500 dark:text-slate-400 mt-3 mb-1" htmlFor="diag-cancel-reason">Reason (required)</label>
              <textarea
                id="diag-cancel-reason"
                className="w-full min-h-[56px] px-2.5 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 text-[12.5px]"
                placeholder="e.g. Ordered by mistake, duplicate test…"
                maxLength={300}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                data-testid="diag-cancel-reason"
              />
            </>
          )}
          ctaLabel={cancelBusy ? 'Cancelling…' : 'Cancel order'}
          cancelLabel="Keep it"
          ctaDisabled={!cancelReason.trim() || cancelBusy}
          onConfirm={confirmCancel}
          onCancel={closeCancel}
        />
      )}
    </div>
  );
}
